import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  LayoutDashboard,
  FileText,
  Calendar as CalendarIcon,
  Vote,
  FileSpreadsheet,
  CheckSquare,
  Plus,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Clock,
  BarChart3,
  CheckCircle,
  XCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VotingModule = () => {
  const { consortiumId } = useParams();
  const [agendas, setAgendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [selectedAgenda, setSelectedAgenda] = useState(null);
  const [results, setResults] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    weight_by_area: true,
  });

  const sidebarItems = [
    {
      section: "Consortium",
      items: [
        { name: "Overview", path: `/consortium/${consortiumId}`, icon: LayoutDashboard },
        { name: "Agreements", path: `/consortium/${consortiumId}/agreements`, icon: FileText },
        { name: "Meetings", path: `/consortium/${consortiumId}/meetings`, icon: CalendarIcon },
        { name: "Voting", path: `/consortium/${consortiumId}/voting`, icon: Vote },
        { name: "RFPs & Bids", path: `/consortium/${consortiumId}/rfps`, icon: FileSpreadsheet },
        { name: "Consent", path: `/consortium/${consortiumId}/consent`, icon: CheckSquare },
      ],
    },
  ];

  useEffect(() => {
    fetchAgendas();
  }, [consortiumId]);

  const fetchAgendas = async () => {
    try {
      const response = await axios.get(`${API}/votes/agendas?consortium_id=${consortiumId}`);
      setAgendas(response.data);
    } catch (error) {
      console.error("Failed to fetch agendas:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgenda = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/votes/agendas`, {
        consortium_id: consortiumId,
        title: formData.title,
        description: formData.description,
        voting_start: startDate.toISOString(),
        voting_end: endDate.toISOString(),
        weight_by_area: formData.weight_by_area,
      });
      toast.success("Voting agenda created!");
      setDialogOpen(false);
      setFormData({ title: "", description: "", weight_by_area: true });
      fetchAgendas();
    } catch (error) {
      toast.error("Failed to create voting agenda");
    }
  };

  const handleCastVote = async (agendaId, vote) => {
    try {
      await axios.post(`${API}/votes/cast`, {
        agenda_id: agendaId,
        vote: vote,
      });
      toast.success("Vote cast successfully!");
      fetchAgendas();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cast vote");
    }
  };

  const handleViewResults = async (agenda) => {
    try {
      const response = await axios.get(`${API}/votes/agendas/${agenda.id}/results`);
      setResults(response.data);
      setSelectedAgenda(agenda);
      setResultsOpen(true);
    } catch (error) {
      toast.error("Failed to fetch results");
    }
  };

  const getStatusBadge = (agenda) => {
    const now = new Date();
    const start = new Date(agenda.voting_start);
    const end = new Date(agenda.voting_end);

    if (now < start) {
      return <Badge variant="outline">Upcoming</Badge>;
    } else if (now >= start && now <= end) {
      return <Badge className="bg-[#4E80FF]">Active</Badge>;
    } else {
      return <Badge variant="secondary">Closed</Badge>;
    }
  };

  const isVotingActive = (agenda) => {
    const now = new Date();
    const start = new Date(agenda.voting_start);
    const end = new Date(agenda.voting_end);
    return now >= start && now <= end;
  };

  return (
    <DashboardLayout sidebarItems={sidebarItems}>
      <div className="space-y-6" data-testid="voting-module">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Voting Process Management
            </h1>
            <p className="text-slate-600">
              Create and manage consortium voting agendas
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-voting-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Voting Agenda
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Voting Agenda</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAgenda} className="space-y-4">
                <div className="space-y-2">
                  <Label>Agenda Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="e.g., Approve Developer Selection"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Detailed description of what members are voting on"
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Voting Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {format(startDate, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={(date) => date && setStartDate(date)}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label>Voting End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {format(endDate, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={(date) => date && setEndDate(date)}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  <Switch
                    checked={formData.weight_by_area}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, weight_by_area: checked })
                    }
                  />
                  <div>
                    <Label>Weight votes by land area</Label>
                    <p className="text-sm text-slate-500">
                      Votes will be weighted based on each member's land contribution
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary">
                    Create Agenda
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
          </div>
        ) : agendas.length > 0 ? (
          <div className="space-y-4">
            {agendas.map((agenda) => (
              <Card key={agenda.id} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Vote className="w-6 h-6 text-[#4E80FF]" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-slate-900">
                          {agenda.title}
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                          {agenda.description}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-2">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(agenda.voting_start).toLocaleDateString()} -{" "}
                            {new Date(agenda.voting_end).toLocaleDateString()}
                          </span>
                          <span>
                            {agenda.weight_by_area ? "Area-weighted" : "Equal votes"}
                          </span>
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(agenda)}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-slate-500">
                      {agenda.votes?.length || 0} votes cast
                    </div>
                    <div className="flex gap-2">
                      {isVotingActive(agenda) && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-200 hover:bg-blue-50"
                            onClick={() => handleCastVote(agenda.id, "assent")}
                            data-testid={`vote-assent-${agenda.id}`}
                          >
                            <ThumbsUp className="w-4 h-4 mr-1" />
                            Assent
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleCastVote(agenda.id, "dissent")}
                            data-testid={`vote-dissent-${agenda.id}`}
                          >
                            <ThumbsDown className="w-4 h-4 mr-1" />
                            Dissent
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCastVote(agenda.id, "abstain")}
                            data-testid={`vote-abstain-${agenda.id}`}
                          >
                            <Minus className="w-4 h-4 mr-1" />
                            Abstain
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewResults(agenda)}
                      >
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Results
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <Vote className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                No Voting Agendas
              </h3>
              <p className="text-slate-500 mb-6">
                Create a voting agenda to enable consortium decision-making.
              </p>
              <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
                Create Voting Agenda
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Results Dialog */}
        <Dialog open={resultsOpen} onOpenChange={setResultsOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Voting Results</DialogTitle>
            </DialogHeader>
            {results && selectedAgenda && (
              <div className="space-y-6">
                <h3 className="font-heading font-semibold text-slate-900">
                  {selectedAgenda.title}
                </h3>

                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        Assent
                      </span>
                      <span className="font-medium">
                        {results.assent_percentage?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <Progress value={results.assent_percentage || 0} className="h-3 bg-slate-200" />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="flex items-center gap-1 text-red-600">
                        <XCircle className="w-4 h-4" />
                        Dissent
                      </span>
                      <span className="font-medium">
                        {results.dissent_percentage?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <Progress value={results.dissent_percentage || 0} className="h-3 bg-slate-200" />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="flex items-center gap-1 text-slate-500">
                        <Minus className="w-4 h-4" />
                        Abstain
                      </span>
                      <span className="font-medium">
                        {results.abstain_percentage?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <Progress value={results.abstain_percentage || 0} className="h-3 bg-slate-200" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">
                      {results.vote_count || 0}
                    </p>
                    <p className="text-sm text-slate-500">Total Votes</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">
                      {results.total_weight?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-slate-500">Total Weight (sqm)</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default VotingModule;
